/**
 * taskTreeFilter — a ÚNICA implementação da regra de retenção (ADR-2).
 *
 * O backend NÃO implementa esta regra. Ele faz o fecho de ancestrais (operação
 * de conjunto) e ROTULA cada nó com `isFilterContext`. Este módulo compõe o
 * veredito do servidor com os predicados client-side:
 *
 *   manter(n) = casa(n) ∨ ∃c ∈ filhos(n) : manter(c)
 *   contexto(n) = manter(n) ∧ ¬casa(n)
 *   casa(n) = (n.isFilterContext !== true) ∧ statusOk(n) ∧ buscaOk(n)
 *             └──── veredito do servidor ────┘  └──── client-side ────┘
 *
 * O front NUNCA reavalia um predicado server-side (responsável, cliente, tag,
 * data) — ele lê o veredito. Reimplementá-los criaria a segunda implementação
 * que a spec proíbe.
 *
 * Semântica da chave `isFilterContext` (contrato de `GET /tasks`):
 *   ausente → não há filtro server-side ativo, OU o payload não veio deste
 *             endpoint (injeção via `setTasks`) ⇒ tratar como "casou"
 *   false   → há filtro ativo e o nó casa os predicados server-side
 *   true    → o nó está aqui apenas como ancestral de um match (pai-contexto)
 *
 * ★ Contrato de identidade (R1, contratual — não conselho): `pruneTaskTree`
 * usa structural sharing. Devolve a MESMA referência quando não há filtro e
 * quando nada mudou numa subárvore. Reconstruir nós invalidaria a identidade
 * de todos os ancestrais e forçaria re-render de `task-item` +
 * `task-inline-subtask-row` a cada tecla digitada na busca.
 *
 * A rotulagem de contexto vive num `Set` FORA dos nós — nenhum nó é
 * reconstruído só para carregar rótulo.
 */

import { normalizeText } from '@utils/Format'
import { isAnyTaskFilterActive } from '@modules/tarefas/utils/filterQuery'
import { taskStatusId } from '@modules/tarefas/utils/optimisticStatusMove'

// Resolve o status do nó com a MESMA semântica de `_applyStatusFilter`
// (`store/getters.js`): compara `statusId` (com fallback para `id_status`)
// contra o conjunto selecionado — NÃO compara `category`. Usar categoria aqui
// mudaria o significado do filtro de status e violaria o AC-5.1.
function _statusOk(node, allowedStatusIds) {
  const statusId = taskStatusId(node)
  return statusId != null && allowedStatusIds[statusId] === true
}

// Busca insensível a acento e caixa sobre o título, reusando a normalização
// vigente da plataforma (`normalizeText`) — reuso obrigatório (AC-4.2).
function _searchOk(node, needle) {
  const title = node.title || ''
  return normalizeText(title).includes(needle)
}

/**
 * Compõe o predicado `casa()` a partir do veredito do servidor, do status e da
 * busca.
 *
 * @param {Object} state estado do módulo `tarefas`
 * @returns {((node: Object) => Boolean) | null} `null` quando NENHUMA das 7
 *   fontes de filtro está ativa — nesse caso a poda é um no-op e a árvore
 *   original é devolvida por identidade.
 *
 * @example
 *   const predicate = buildTaskMatchPredicate(state)
 *   const { tree, contextIds, matchedCount } = pruneTaskTree(state.tasks, predicate)
 */
export function buildTaskMatchPredicate(state) {
  if (!isAnyTaskFilterActive(state)) return null

  const rawQuery =
    state && typeof state.searchQuery === 'string' ? state.searchQuery : ''
  const needle = normalizeText(rawQuery)
  const statusIds =
    state && Array.isArray(state.statusFilter) ? state.statusFilter : []

  const allowedStatusIds = {}
  for (const statusId of statusIds) allowedStatusIds[statusId] = true

  const hasSearch = !!needle
  const hasStatus = statusIds.length > 0

  return function (node) {
    if (!node) return false
    if (node.isFilterContext === true) return false
    if (hasStatus && !_statusOk(node, allowedStatusIds)) return false
    if (hasSearch && !_searchOk(node, needle)) return false
    return true
  }
}

// Poda uma lista de nós devolvendo a MESMA referência quando nada mudou.
// `accumulator` carrega `contextIds` e `matchedCount` para evitar alocar um
// objeto de retorno por nó visitado.
function _pruneNodeList(nodes, predicate, accumulator) {
  const kept = []
  let changed = false
  for (const node of nodes) {
    const prunedNode = _pruneNode(node, predicate, accumulator)
    if (prunedNode === null) {
      changed = true
      continue
    }
    if (prunedNode !== node) changed = true
    kept.push(prunedNode)
  }
  return changed ? kept : nodes
}

// Devolve o nó (mesma referência quando intocado), um clone raso com as
// sub-tarefas podadas, ou `null` quando o nó não deve ser retido.
function _pruneNode(node, predicate, accumulator) {
  if (!node) return null

  const matches = predicate(node) === true
  if (matches) accumulator.matchedCount += 1

  const children = Array.isArray(node.subtarefas) ? node.subtarefas : null
  const prunedChildren = children
    ? _pruneNodeList(children, predicate, accumulator)
    : null
  const hasKeptChild = prunedChildren !== null && prunedChildren.length > 0

  if (!matches && !hasKeptChild) return null
  if (!matches) accumulator.contextIds.add(node.id)

  // Structural sharing: só reconstrói quando a lista de filhas mudou.
  if (prunedChildren === null || prunedChildren === children) return node
  return { ...node, subtarefas: prunedChildren }
}

/**
 * Aplica `manter(n) = casa(n) ∨ ∃c: manter(c)` preservando identidade.
 *
 * @param {Array} nodes árvore de tarefas (raízes com `subtarefas` recursivas)
 * @param {Function|null} predicate saída de `buildTaskMatchPredicate`
 * @returns {{ tree: Array, contextIds: Set<String>, matchedCount: Number }}
 *   `contextIds` traz os nós RETIDOS que não casam (os pais-contexto).
 *   Nenhum caminho lança: nó sem `subtarefas` é folha; status irresolvível
 *   simplesmente não casa.
 *
 * @example
 *   pruneTaskTree(tasks, null).tree === tasks   // sem filtro: identidade
 */
export function pruneTaskTree(nodes, predicate) {
  const isValidTree = Array.isArray(nodes)
  if (typeof predicate !== 'function') {
    return {
      tree: isValidTree ? nodes : [],
      contextIds: new Set(),
      matchedCount: 0,
    }
  }
  if (!isValidTree) {
    return { tree: [], contextIds: new Set(), matchedCount: 0 }
  }

  const accumulator = { contextIds: new Set(), matchedCount: 0 }
  const tree = _pruneNodeList(nodes, predicate, accumulator)
  return {
    tree: tree,
    contextIds: accumulator.contextIds,
    matchedCount: accumulator.matchedCount,
  }
}

/**
 * Traduz o `Set` de pais-contexto num mapa `id → booleano` para um lote de nós.
 *
 * Existe por uma razão só: o gate do chip precisa chegar ao template como
 * PROPRIEDADE (`flags[node.id]`) e não como chamada de função (`Set.has(...)`).
 * Chamada de função em `v-if`/`v-bind` reexecuta a cada re-render — proibido
 * pelo `CLAUDE.md` e caro nas listas densas deste módulo.
 *
 * ★ R1: NÃO toca nos nós nem no array. O structural sharing de `pruneTaskTree`
 * continua valendo e cada linha/card recebe a MESMA referência de nó que o
 * util devolveu. É por isso que o rótulo mora num mapa lateral e não num
 * wrapper `{ node, isContext }`.
 *
 * `nodes` é um lote qualquer, não necessariamente irmãos: `task-list-view` e
 * `task-inline-subtask-row` passam os filhos diretos de um nó; `task-kanban-view`
 * passa os cards de todas as colunas de uma vez. A conta é a mesma.
 *
 * @param {Array} nodes lote de nós a rotular
 * @param {Set<String>} contextIds saída de `pruneTaskTree().contextIds`
 * @param {Boolean} filterActive sem filtro devolve `{}` — zero trabalho a mais
 * @returns {Object} mapa `id → booleano`; `{}` quando não há o que rotular
 */
export function buildContextFlags(nodes, contextIds, filterActive) {
  const flags = {}
  if (!filterActive) return flags
  if (!Array.isArray(nodes)) return flags
  if (!contextIds || typeof contextIds.has !== 'function') return flags
  for (const node of nodes) {
    flags[node.id] = contextIds.has(node.id) === true
  }
  return flags
}
